import React, { useState, useRef } from 'react';
import { doc, updateDoc, arrayUnion, getDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../config/firebaseConfig';
import { toast } from 'react-hot-toast';
import { 
  FaWallet,
  FaTimes,
  FaSave,
  FaCar,
  FaBed,
  FaUtensils,
  FaBox,
  FaEllipsisH,
  FaDollarSign,
  FaFileInvoice,
  FaInfoCircle,
  FaCloudUploadAlt,
  FaImage,
  FaFilePdf,
  FaFile,
  FaTrash,
  FaEye
} from 'react-icons/fa';
import styles from './ExpenseModal.module.css';

const ExpenseModal = ({ show, onHide, visitId, onExpenseAdded }) => {
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    type: '',
    cost: '',
    information: '',
    attachments: []
  });
  const [errors, setErrors] = useState({});
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const expenseTypes = [
    {
      value: 'transportation',
      label: 'Ulaşım',
      icon: FaCar,
      color: '#3182ce'
    },
    {
      value: 'accommodation',
      label: 'Konaklama',
      icon: FaBed,
      color: '#38a169'
    },
    {
      value: 'meal',
      label: 'Yemek',
      icon: FaUtensils,
      color: '#f6ad55'
    },
    {
      value: 'supplies',
      label: 'Malzeme',
      icon: FaBox,
      color: '#9f7aea'
    },
    {
      value: 'other',
      label: 'Diğer',
      icon: FaEllipsisH,
      color: '#718096'
    }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Masraf adı gerekli';
    }
    
    if (!formData.type) {
      newErrors.type = 'Masraf tipi seçilmeli';
    }
    
    if (!formData.cost || parseFloat(formData.cost) <= 0) {
      newErrors.cost = 'Geçerli bir maliyet giriniz';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith('image/')) return FaImage;
    if (fileType === 'application/pdf') return FaFilePdf;
    return FaFile;
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const uploadFileToStorage = async (file) => {
    const timestamp = Date.now();
    const fileName = `expenses/${visitId}/${timestamp}_${file.name}`;
    const storageRef = ref(storage, fileName);
    
    try {
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        name: file.name,
        url: downloadURL,
        type: file.type,
        size: file.size,
        uploadedAt: new Date().toISOString(),
        storagePath: fileName
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const handleFileUpload = async (files) => {
    const fileList = Array.from(files);
    
    // Validate file types and sizes
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf', 'image/webp'];
    const maxSize = 5 * 1024 * 1024; // 5MB
    
    const validFiles = fileList.filter(file => {
      if (!allowedTypes.includes(file.type)) {
        toast.error(`${file.name}: Desteklenmeyen dosya türü. Sadece resim ve PDF dosyaları yükleyebilirsiniz.`);
        return false;
      }
      if (file.size > maxSize) {
        toast.error(`${file.name}: Dosya boyutu çok büyük. Maksimum 5MB yükleyebilirsiniz.`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = validFiles.map(file => uploadFileToStorage(file));
      const uploadedFiles = await Promise.all(uploadPromises);
      
      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...uploadedFiles]
      }));
      
      toast.success(`${uploadedFiles.length} dosya başarıyla yüklendi`);
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Dosya yüklenirken bir hata oluştu');
    } finally {
      setUploading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileUpload(e.target.files);
    }
  };

  const removeAttachment = async (index) => {
    const attachment = formData.attachments[index];
    
    try {
      // Delete from Firebase Storage
      if (attachment.storagePath) {
        const storageRef = ref(storage, attachment.storagePath);
        await deleteObject(storageRef);
      }
      
      // Remove from state
      setFormData(prev => ({
        ...prev,
        attachments: prev.attachments.filter((_, i) => i !== index)
      }));
      
      toast.success('Dosya silindi');
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Dosya silinirken bir hata oluştu');
    }
  };

  const openFilePreview = (url) => {
    window.open(url, '_blank');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Lütfen formu kontrol ediniz');
      return;
    }

    if (!visitId) {
      toast.error('Ziyaret ID bulunamadı');
      return;
    }

    setSaving(true);
    try {
      const visitRef = doc(db, 'Visits', visitId);
      const expenseData = {
        ...formData,
        cost: parseFloat(formData.cost),
        createdAt: new Date().toISOString(),
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}` // Add unique ID for expense
      };

      console.log('Saving expense to Firebase:', expenseData);
      console.log('Visit ID:', visitId);

      await updateDoc(visitRef, {
        expenses: arrayUnion(expenseData),
        lastUpdated: new Date().toISOString()
      });

      // Verify the expense was saved by fetching the document
      const updatedDoc = await getDoc(visitRef);
      if (updatedDoc.exists()) {
        const updatedData = updatedDoc.data();
        const savedExpense = updatedData.expenses?.find(exp => exp.id === expenseData.id);
        if (savedExpense) {
          console.log('Expense verified in Firebase:', savedExpense);
          toast.success('Masraf başarıyla eklendi ve kaydedildi');
          onExpenseAdded(expenseData);
          resetForm();
          onHide();
        } else {
          throw new Error('Masraf kaydedildi ancak doğrulanamadı');
        }
      } else {
        throw new Error('Ziyaret dokümanı bulunamadı');
      }
    } catch (error) {
      console.error('Error adding expense:', error);
      console.error('Visit ID was:', visitId);
      console.error('Expense data was:', formData);
      toast.error('Masraf eklenirken bir hata oluştu: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      type: '',
      cost: '',
      information: '',
      attachments: []
    });
    setErrors({});
  };

  const handleClose = () => {
    resetForm();
    onHide();
  };

  const handleTypeSelect = (typeValue) => {
    handleInputChange('type', typeValue);
  };

  if (!show) return null;

  return (
    <div className={styles.modalOverlay} onClick={handleClose}>
      <div className={styles.modalContent} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.headerContent}>
            <FaWallet className={styles.headerIcon} />
            <h2 className={styles.modalTitle}>Masraf Ekle</h2>
          </div>
          <button 
            type="button" 
            className={styles.closeButton} 
            onClick={handleClose}
            aria-label="Kapat"
          >
            <FaTimes />
          </button>
        </div>
        
        <div className={styles.modalBody}>
                    <form onSubmit={handleSubmit} className={styles.formContainer}>
            <div className={styles.formRow}>
              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <FaFileInvoice className={styles.labelIcon} />
                  Masraf Adı
                </label>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    className={styles.input}
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Masraf adını giriniz"
                  />
                </div>
                {errors.name && <div className={styles.errorText}>{errors.name}</div>}
              </div>

              <div className={styles.formGroup}>
                <label className={styles.label}>
                  <FaDollarSign className={styles.labelIcon} />
                  Maliyet
                </label>
                <div className={styles.priceWrapper}>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className={styles.input}
                    value={formData.cost}
                    onChange={(e) => handleInputChange('cost', e.target.value)}
                    placeholder="0.00"
                  />
                  <span className={styles.currencySymbol}>₺</span>
                </div>
                {errors.cost && <div className={styles.errorText}>{errors.cost}</div>}
              </div>
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                <FaBox className={styles.labelIcon} />
                Masraf Tipi
              </label>
              <div className={styles.expenseTypeGrid}>
                {expenseTypes.map((type) => {
                  const IconComponent = type.icon;
                  return (
                    <div
                      key={type.value}
                      className={`${styles.typeOption} ${formData.type === type.value ? styles.selected : ''}`}
                      onClick={() => handleTypeSelect(type.value)}
                    >
                      <IconComponent 
                        className={styles.typeIcon} 
                        style={{ color: type.color }}
                      />
                      <span className={styles.typeLabel}>{type.label}</span>
                    </div>
                  );
                })}
              </div>
              {errors.type && <div className={styles.errorText}>{errors.type}</div>}
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                <FaInfoCircle className={styles.labelIcon} />
                Bilgi <span style={{ fontWeight: 400, color: '#718096' }}>(Opsiyonel)</span>
              </label>
              <textarea
                className={styles.textarea}
                value={formData.information}
                onChange={(e) => handleInputChange('information', e.target.value)}
                placeholder="Masraf hakkında ek bilgi giriniz..."
                rows={3}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                <FaCloudUploadAlt className={styles.labelIcon} />
                Dosya Ekle <span style={{ fontWeight: 400, color: '#718096' }}>(Fatura, Makbuz vb.)</span>
              </label>
              
              <div 
                className={`${styles.uploadArea} ${dragActive ? styles.dragActive : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
              >
                <div className={styles.uploadContent}>
                  <FaCloudUploadAlt className={styles.uploadIcon} />
                  <p className={styles.uploadText}>
                    Dosyaları buraya sürükleyin veya <span className={styles.uploadLink}>seçmek için tıklayın</span>
                  </p>
                  <p className={styles.uploadSubtext}>
                    PNG, JPG, WEBP, PDF - Maksimum 5MB
                  </p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleFileSelect}
                  className={styles.hiddenInput}
                />
              </div>

              {uploading && (
                <div className={styles.uploadingIndicator}>
                  <div className={styles.loadingSpinner} />
                  Dosyalar yükleniyor...
                </div>
              )}

              {formData.attachments.length > 0 && (
                <div className={styles.attachmentsList}>
                  {formData.attachments.map((attachment, index) => {
                    const FileIcon = getFileIcon(attachment.type);
                    return (
                      <div key={index} className={styles.attachmentItem}>
                        <div className={styles.attachmentInfo}>
                          <FileIcon className={styles.attachmentIcon} />
                          <div className={styles.attachmentDetails}>
                            <span className={styles.attachmentName}>{attachment.name}</span>
                            <span className={styles.attachmentSize}>{formatFileSize(attachment.size)}</span>
                          </div>
                        </div>
                        <div className={styles.attachmentActions}>
                          <button
                            type="button"
                            className={styles.attachmentButton}
                            onClick={() => openFilePreview(attachment.url)}
                            title="Önizle"
                          >
                            <FaEye />
                          </button>
                          <button
                            type="button"
                            className={styles.attachmentButton}
                            onClick={() => removeAttachment(index)}
                            title="Sil"
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </form>
        </div>
        
        <div className={styles.buttonGroup}>
          <button 
            type="button"
            className={`${styles.button} ${styles.cancelButton}`}
            onClick={handleClose}
          >
            <FaTimes />
            İptal
          </button>
          <button 
            type="submit"
            className={`${styles.button} ${styles.saveButton}`}
            onClick={handleSubmit}
            disabled={saving || uploading}
          >
            {saving ? (
              <>
                <div className={styles.loadingSpinner} />
                Kaydediliyor...
              </>
            ) : (
              <>
                <FaSave />
                Kaydet
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseModal; 